import { register, Hanko } from '@teamhanko/hanko-elements';
import type { AnyState, FlowName, StateCreateConfig } from '@teamhanko/hanko-frontend-sdk';
import { de } from '@teamhanko/hanko-elements/i18n/de';
import { en } from '@teamhanko/hanko-elements/i18n/en';
import { fr } from '@teamhanko/hanko-elements/i18n/fr';
import { nl } from '@teamhanko/hanko-elements/i18n/nl';
import { es } from '../i18n/hanko/es';
import { tr } from '../i18n/hanko/tr';

export type HankoEventName =
    | 'sessionCreated'
    | 'sessionExpired'
    | 'userLoggedOut'
    | 'accountDeleted';

export interface HankoClient {
    logout(): Promise<void>;
    on(event: HankoEventName, handler: () => void): () => void;
}

export type RegisteredHankoClient = HankoClient & {
    createState: (flowName: FlowName, config?: StateCreateConfig) => Promise<AnyState>;
};

let hankoClientInstance: RegisteredHankoClient | null = null;
let hankoRegistrationPromise: Promise<RegisteredHankoClient> | null = null;
const hankoClientReadyListeners = new Set<(client: HankoClient) => void>();

export const getHankoApiUrl = (): string => {
    return import.meta.env.VITE_HANKO_API_URL || 'http://localhost:8000';
};

export const getHankoCookieDomain = (): string | undefined => {
    if (typeof window !== 'undefined' && window.location.hostname.endsWith('minifil.com')) {
        return 'minifil.com';
    }
    return undefined;
};

const notifyHankoClientReadyListener = (
    listener: (client: HankoClient) => void,
    client: RegisteredHankoClient,
): void => {
    try {
        listener(client);
    } catch {
        console.error('Hanko client readiness listener failed');
    }
};

const notifyHankoClientReady = (client: RegisteredHankoClient): void => {
    hankoClientReadyListeners.forEach((listener) => notifyHankoClientReadyListener(listener, client));
};

export function onHankoClientReady(listener: (client: HankoClient) => void): () => void {
    hankoClientReadyListeners.add(listener);
    if (hankoClientInstance) {
        notifyHankoClientReadyListener(listener, hankoClientInstance);
    }

    return () => {
        hankoClientReadyListeners.delete(listener);
    };
}

const createHankoClient = (hanko: Hanko): RegisteredHankoClient => ({
    createState: (flowName, config) => hanko.createState(flowName, config),
    logout: async () => {
        await hanko.logout();
    },
    on: (event: HankoEventName, handler: () => void) => {
        switch (event) {
            case 'sessionCreated':
                return hanko.onSessionCreated(handler);
            case 'sessionExpired':
                return hanko.onSessionExpired(handler);
            case 'userLoggedOut':
                return hanko.onUserLoggedOut(handler);
            case 'accountDeleted':
                return hanko.onUserDeleted(handler);
            default:
                return () => {};
        }
    },
});

export function registerHankoElements(apiUrl: string): Promise<RegisteredHankoClient> {
    if (!hankoRegistrationPromise) {
        const cookieDomain = getHankoCookieDomain();
        hankoRegistrationPromise = Promise.resolve()
            .then(() =>
                register(apiUrl, {
                    translations: { en, es, fr, de, nl, tr },
                    fallbackLanguage: 'en',
                    ...(cookieDomain ? { cookieDomain } : {}),
                }),
            )
            .then(({ hanko }) => {
                hankoClientInstance = createHankoClient(hanko);
                notifyHankoClientReady(hankoClientInstance);
                return hankoClientInstance;
            })
            .catch((err: unknown) => {
                hankoRegistrationPromise = null;
                hankoClientInstance = null;
                console.error('Failed to register Hanko elements', err);
                throw err;
            });
    }

    return hankoRegistrationPromise;
}

export async function getHankoClient(apiUrl: string): Promise<RegisteredHankoClient> {
    if (hankoClientInstance) {
        return hankoClientInstance;
    }

    return registerHankoElements(apiUrl);
}

export function _resetHankoState(): void {
    hankoClientInstance = null;
    hankoRegistrationPromise = null;
    hankoClientReadyListeners.clear();
}
